
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';
import store from '@/store';
import FondoDiagonal from '@/componentes/UI/FondoDiagonal.vue';
import ProgresoPasos from '@/componentes/ProgresoPasos.vue';
import { DatosContactoI, getDatosContacto, patchDatosContacto } from '@/assets/api/matricula';
import {
  RegionI, ComunaI, getRegiones, PaisI, getPaises,
} from '@/assets/api/general';

@Component({ components: { FondoDiagonal, ProgresoPasos } })
export default class PerfilEstudiante extends Vue {
  datosContacto: DatosContactoI = {} as DatosContactoI;

  regiones: RegionI[] = [];

  region: RegionI | null = null;

  comuna: ComunaI | null = null;

  paises: PaisI[] = [];

  nacionalidad: PaisI | null = null;

  confirmarCorreo = '';

  generos = ['masculino', 'femenino', 'otro']

  otroGenero = '';

  cargando = false;

  error = '';

  check_nombre_social = false;

  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext<PerfilEstudiante>) {
    const promesaRegiones = getRegiones();
    const promesaPaises = getPaises();
    const promesadatos = getDatosContacto(store.getters.idEstudiante);
    Promise.all([promesadatos, promesaRegiones, promesaPaises])
      .then(([datosContacto, regiones, paises]) => {
        next((vm) => {
          /* eslint-disable no-param-reassign */
          vm.datosContacto = datosContacto;
          vm.regiones = regiones;
          vm.paises = paises;
          regiones.forEach((region) => {
            region.comunas.forEach((comuna) => {
              if (comuna.id === datosContacto.direccion_comuna) {
                vm.comuna = comuna;
                vm.region = region;
              }
            });
          });
          paises.forEach((nacionalidad) => {
            if (nacionalidad.id === datosContacto.nacionalidad) {
              vm.nacionalidad = nacionalidad;
            }
          });
          // detectar otros generos
          if (datosContacto.genero !== '' && !vm.generos.includes(datosContacto.genero)) {
            vm.otroGenero = datosContacto.genero;
            vm.datosContacto.genero = 'otro';
          }
          // rellenar mail si se están modificando los datos
          if (store.getters.etapaEstudiante > 3) {
            vm.confirmarCorreo = datosContacto.mail_secundario;
          }
          // Set social name checkbox
          vm.check_nombre_social = Boolean(datosContacto.nombre_social);
          /* eslint-enable no-param-reassign */
        });
      })
      .catch(() => {
        // ✅ En caso de error, redirigir al inicio
        next({ name: store.getters.linkInicio });
      });
  }

  // Event handlers
  handleRegionChange(): void {
    this.comuna = null;
  }

  // Watchers for clearing errors
  @Watch('comuna')
  onComunaChange() {
    if (this.error.includes('comuna')) {
      this.error = '';
    }
  }

  @Watch('nacionalidad')
  onNacionalityChange() {
    if (this.error.includes('nacionalidad')) {
      this.error = '';
    }
  }

  @Watch('datosContacto.mail_secundario')
  @Watch('confirmarCorreo')
  onEmailChange() {
    if (this.error.includes('correo')) {
      this.error = '';
    }
  }

  @Watch('datosContacto.emergencia_telefono_celular')
  @Watch('datosContacto.emergencia_telefono_laboral')
  @Watch('datosContacto.emergencia_telefono_fijo')
  onEmergencyPhoneChange() {
    if (this.error.includes('teléfono')) {
      this.error = '';
    }
  }

  // Validation methods
  private validarComuna(): boolean {
    if (!this.comuna) {
      this.error = 'Por favor selecciona tu comuna';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validarNacionalidad(): boolean {
    if (!this.nacionalidad) {
      this.error = 'Por favor selecciona tu nacionalidad';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validarConfirmarCorreo(): boolean {
    if (!this.confirmarCorreo) {
      this.error = 'Por favor confirma tu correo';
      this.scrollToTop();
      return false;
    }
    if (this.confirmarCorreo !== this.datosContacto.mail_secundario) {
      this.error = 'Los correos no coinciden, verifica que los ingresaste correctamente';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validarEmergenciaTelefono(): boolean {
    const hasPhone = !!(
      this.datosContacto.emergencia_telefono_celular
      || this.datosContacto.emergencia_telefono_laboral
      || this.datosContacto.emergencia_telefono_fijo
    );
    if (!hasPhone) {
      this.error = 'Por favor ingresa al menos un teléfono de tu contacto de emergencia';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Submit handler
  async enviar() {
    // validar datos
    if (!(this.validarComuna() && this.validarConfirmarCorreo()
      && this.validarEmergenciaTelefono() && this.validarNacionalidad())) {
      return;
    }

    if (this.comuna) {
      this.datosContacto.direccion_comuna = this.comuna.id;
    }
    if (this.nacionalidad) {
      this.datosContacto.nacionalidad = this.nacionalidad.id;
    }
    if (this.datosContacto.genero === 'otro') {
      this.datosContacto.genero = this.otroGenero;
    }

    if (store.getters.idEstudiante) {
      this.cargando = true;
      this.error = '';

      try {
        const respuesta = await patchDatosContacto(store.getters.idEstudiante, this.datosContacto);
        store.commit('actualizarNombre', respuesta.nombre_social);
        store.commit('actualizarEtapa', 2);

        // ✅ Forzar navegación después de actualizar store
        await this.$nextTick();
        await this.$router.push({ name: store.getters.linkPagoMatricula });
      } catch (error) {
        this.cargando = false;
        this.error = 'Lo sentimos, ha ocurrido un error en el sistema. Por favor intenta nuevamente y si el problema persiste ponte en contacto con el encargado de matrícula';
      }
    }
  }
}
